import moment from 'moment';
import * as React from 'react';
import { useState } from 'react';
import { generateInvoiceMutation } from '../../graphql/mutations';
import { useLazyQuery, useMutation } from '@apollo/client';
import { getInvoiceDataQuery } from '../../graphql/queries';
import { centsToPrice } from '@/utils/formatter';

/* @ts-ignore */
import { xmlToJSON } from '@/utils/xmlToJson';
import { EditSection } from '../../components/Layout';
import i18n from '@/utils/i18n';
import { IconButton } from '../../components/Button/Button';
import { FaEye, FaFileInvoiceDollar } from 'react-icons/all'; /* @ts-ignore */
import { InvoiceMonthSelector } from '../../components/InvoiceMonthSelector';

const initialYear = moment()
  .subtract(1, 'months')
  .endOf('month')
  .year()
  .toString();
const initialMonth = (
  moment().subtract(1, 'months').endOf('month').month() + 1
).toString();

type InvoiceActionProps = {
  storeId?: string | null;
  currency?: string | null;
  match?: { params: { storeId: string } };
};

type InvoiceResult = {
  result: string;
  url: string;
};

export const InvoiceActions: React.FC<InvoiceActionProps> = ({
  storeId,
  match,
  currency,
}) => {
  const sId = match?.params.storeId || storeId;

  const [selectedYear, setSelectedYear] = useState(initialYear);
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);

  const [generateInvoice, { loading: generatingInvoice }] = useMutation(
    generateInvoiceMutation,
  );
  const [
    getInvoiceData,
    {
      loading: gettingInvoiceData,
      data: invoicePreviewData = { getInvoiceData: null },
      error: getInvoiceDataError = { graphQLErrors: [] },
    },
  ] = useLazyQuery(getInvoiceDataQuery);
  const [invoiceResult, setInvoiceResult] = useState<InvoiceResult | null>(
    null,
  );
  const [errors, setErrors] = useState<string[]>([]);

  const invoicePreview: any = invoicePreviewData.getInvoiceData;
  const allErrors = [...errors, ...getInvoiceDataError.graphQLErrors];

  async function onShowInvoicePreviewClick() {
    setErrors([]);
    setInvoiceResult(null);
    await getInvoiceData({
      variables: {
        store_id: sId,
        year: Number(selectedYear),
        month: Number(selectedMonth),
      },
    });
  }

  async function onGenerateInvoiceClick() {
    setErrors([]);
    setInvoiceResult(null);
    try {
      await generateInvoice({
        variables: {
          store_id: sId,
          year: Number(selectedYear),
          month: Number(selectedMonth),
        },
        update: (
          cache,
          { data: { generateInvoice: mutationInvoiceResult } },
        ) => {
          setInvoiceResult(mutationInvoiceResult);
        },
      });
    } catch (error) {
      const { graphQLErrors } = error as any;
      setErrors(graphQLErrors as any);
      console.log(graphQLErrors);
    }
  }

  const loading = generatingInvoice || gettingInvoiceData;

  let invoicePreviewXml = null;
  if (invoicePreview) {
    invoicePreviewXml = xmlToJSON.parseString(
      '<invoice>' + invoicePreview.invoice_xml + '</invoice>',
      //@ts-ignore
    ).invoice;
  }

  // @ts-ignore
  return (
    <EditSection title={i18n.t('Invoices:invoiceGeneration')}>
      <div className="row">
        <InvoiceMonthSelector
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          setSelectedMonth={setSelectedMonth}
          setSelectedYear={setSelectedYear}
        />
      </div>
      <div className="mb-4">
        <IconButton
          className="mb-2 me-2"
          icon={<FaEye />}
          label={i18n.t('Invoices:invoicePreview')}
          onClick={onShowInvoicePreviewClick}
          isLoading={loading}
        />
        <IconButton
          className="mb-2 me-2"
          icon={<FaFileInvoiceDollar />}
          label={i18n.t('Invoices:generateInvoice')}
          onClick={onGenerateInvoiceClick}
          isLoading={loading}
        />
      </div>

      {allErrors.length > 0 && (
        <div className="mb-2">
          {allErrors.map((error) => (
            <div className="alert alert-danger" role="alert">
              {error?.toString?.()}
            </div>
          ))}
        </div>
      )}
      {invoiceResult && (
        <div className="mb-2">
          <table className="table-striped bg-light mb-3 table w-auto">
            <tbody>
              <tr>
                <td>Result</td>
                <td>{invoiceResult.result}</td>
              </tr>
              {invoiceResult.url && (
                <tr>
                  <td>Link</td>
                  <td>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={invoiceResult.url}
                    >
                      {invoiceResult.url}
                    </a>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {invoicePreview && !loading && !invoiceResult && (
        <div className="mb-2">
          <table className="table-striped bg-light mb-3 table w-auto">
            <tbody>
              <tr>
                <td colSpan={2}>
                  <b>Invoice Preview</b>
                </td>
              </tr>
              <tr>
                <td>total_platform_fee</td>
                <td className="numbers-formatted">
                  {centsToPrice(invoicePreview.total_platform_fee, currency)}
                </td>
              </tr>
              <tr>
                <td>custom_items_total</td>
                <td className="numbers-formatted">
                  {centsToPrice(invoicePreview.custom_items_total, currency)}
                </td>
              </tr>
              <tr>
                <td>transaction_cost_total</td>
                <td className="numbers-formatted">
                  {centsToPrice(
                    invoicePreview.transaction_cost_total,
                    currency,
                  )}
                </td>
              </tr>
              <tr>
                <td>monthly_fee</td>
                <td className="numbers-formatted">
                  {centsToPrice(invoicePreview.monthly_fee, currency)}
                </td>
              </tr>
              <tr>
                <td>onboarding_fee_vytal</td>
                <td className="numbers-formatted">
                  {centsToPrice(invoicePreview.onboarding_fee_vytal, currency)}
                </td>
              </tr>
              <tr>
                <td>total_minus_taxes</td>
                <td className="numbers-formatted">
                  {centsToPrice(invoicePreview.total_minus_taxes, currency)}
                </td>
              </tr>
              <tr>
                <td>tax_fee</td>
                <td className="numbers-formatted">
                  {centsToPrice(invoicePreview.tax_fee, currency)}
                </td>
              </tr>
              <tr>
                <td>total</td>
                <td className="numbers-formatted">
                  {centsToPrice(
                    invoicePreview.total_minus_taxes + invoicePreview.tax_fee,
                    currency,
                  )}
                </td>
              </tr>
              <tr>
                <td>store_cards_sold_total</td>
                <td className="numbers-formatted">
                  {centsToPrice(
                    invoicePreview.store_cards_sold_total,
                    currency,
                  )}
                </td>
              </tr>
              <tr>
                <td>would_generate_invoice</td>
                <td className="numbers-formatted">
                  {invoicePreview.would_generate_invoice ? 'yes' : 'no'}
                </td>
              </tr>
              <tr>
                <td colSpan={2}>
                  {invoicePreviewXml['invoice-item'].map((invoiceItem: any) => (
                    <>
                      <b>{invoiceItem.title.res}</b>
                      <br />
                      {invoiceItem.description && invoiceItem.description.res}
                      <br />
                      {invoiceItem.unit_price && (
                        <>
                          {invoiceItem.quantity.res} x{' '}
                          {centsToPrice(
                            invoiceItem.unit_price.res * 100,
                            currency,
                          )}
                          {' = '}
                          <b>
                            {centsToPrice(
                              invoiceItem.quantity.res *
                                invoiceItem.unit_price.res *
                                100,
                              currency,
                            )}
                          </b>
                          {/* <CalculationNote> (tax: {invoiceItem.tax_rate.res} % - reduction: {formatBillomatPrice(invoiceItem.reduction.res)} €)</CalculationNote> */}
                        </>
                      )}
                      <br />
                      <br />
                    </>
                  ))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </EditSection>
  );
};
